import type { Control } from "./Control.js";
import type { Point } from "../../common/geometry.js";

export interface ModifierKeys {
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

export interface MouseEventArgs extends ModifierKeys {
  /** Position in the target control's own coordinate space. */
  x: number;
  y: number;
  /** Position in canvas (screen) coordinates. */
  screen: Point;
  button: number;
  buttons: number;
  /** 1 for a single click, 2 for the second click of a double-click, etc. */
  clickCount: number;
  /** Set by a handler to stop further processing (bubbling, default action). */
  handled: boolean;
}

export interface WheelEventArgs extends MouseEventArgs {
  deltaX: number;
  deltaY: number;
}

/**
 * What is being dragged.
 *
 * `kind` exists so a drop target can refuse a payload without having to
 * understand it — an inventory takes "item" and ignores "window-tab" — and
 * `data` is whatever the two ends have agreed on between them.
 */
export interface DragPayload {
  readonly kind: string;
  readonly data: unknown;
  /** Where the drag started, and who hears `dragend`. */
  readonly source: Control | null;
}

export interface DragEventArgs extends MouseEventArgs {
  payload: DragPayload;
  /**
   * Whether the payload is being taken. True on arrival at a control that
   * said it could accept the drop; a handler clears it to refuse after all.
   */
  accepted: boolean;
}

export interface KeyEventArgs extends ModifierKeys {
  key: string;
  code: string;
  repeat: boolean;
  handled: boolean;
}

export interface TextInputArgs {
  text: string;
  handled: boolean;
  /**
   * True when this text arrived from the clipboard rather than from a person typing.
   *
   * A paste reaches a control as committed text like any other - which is right, since a
   * control that had to handle it separately would get the ordinary case wrong - but an editor
   * that reformats what it is given has to know which it was: doing it per keystroke would
   * rewrite the file under someone mid-word.
   */
  paste?: boolean;
}

/**
 * Every event a Webwin control can raise.
 *
 * This is deliberately one flat interface rather than a generic parameter per
 * control: it keeps `Container.controls` a plain `Control[]` and avoids
 * variance headaches. The cost is that `listBox.on("textchanged")` type-checks
 * even though a ListBox never raises it.
 */
export interface ControlEvents {
  pointerdown: MouseEventArgs;
  pointermove: MouseEventArgs;
  pointerup: MouseEventArgs;
  pointerenter: MouseEventArgs;
  pointerleave: MouseEventArgs;
  click: MouseEventArgs;
  doubleclick: MouseEventArgs;
  contextmenu: MouseEventArgs;
  wheel: WheelEventArgs;

  keydown: KeyEventArgs;
  keyup: KeyEventArgs;
  textinput: TextInputArgs;

  /**
   * Drag and drop. Only a control whose `canAcceptDrop` said yes hears these;
   * the pointer passing over anything else during a drag is silent.
   */
  dragenter: DragEventArgs;
  dragover: DragEventArgs;
  dragleave: DragEventArgs;
  drop: DragEventArgs;
  /** Raised on the payload's source when the drag ends, however it ended. */
  dragend: { payload: DragPayload; accepted: boolean };

  focus: void;
  blur: void;

  /** Bounds changed. */
  resize: void;
  move: void;
  visiblechanged: void;
  enabledchanged: void;

  /** Value-bearing controls: TextBox, CheckBox, RadioButton, ScrollBar. */
  change: void;
  textchanged: void;
  /** ListBox / ListView. */
  selectionchanged: void;
  itemactivate: number;
  /** ProgressBar, and anything else carrying a single numeric value. */
  valuechanged: void;
  /** TreeView: a branch opened or closed. */
  expandedchanged: void;
  /** TreeView: a node's check state was toggled. */
  checkedchanged: void;
  /** DataGrid: a cell's value was written back. */
  cellchanged: void;
  /** DataGrid: an edit was refused because the text did not parse. */
  editrejected: void;
  /** DataGrid: the sort column or direction changed. */
  sortchanged: void;
  /** SplitContainer: the bar was moved, by drag, key or code. */
  splittermoved: void;
  /** SplitContainer: a pane was collapsed or restored. */
  collapsedchanged: void;
  /** TabControl: a tab's close button was pressed. Set cancel to keep it. */
  tabclosing: { index: number; cancel: boolean };
  /** TabControl: a tab was closed, by index as it was before removal. */
  tabclosed: number;
  /**
   * FlipCard: the card started turning, and finished turning.
   *
   * `flipping` is raised the moment the destination is decided, so a handler
   * reading `showing` sees the face being turned *to*. `flipped` is raised on
   * the frame the turn runs out, or immediately after `flipping` when there
   * was no turn to run — a cut, or a card with no desktop to animate against.
   */
  flipping: void;
  flipped: void;
  /**
   * Motion: an animation group started, or ran to its end. The payload is the group's name.
   * Raised just after the frame that noticed, never during one, so a handler may change the tree.
   * A looping group, and a held one such as a hover reaction, never finishes.
   */
  animationplay: string;
  animationfinished: string;
  /** ScrollViewer / scrolling lists. */
  scroll: void;
  zoom: void;

  /**
   * VideoControl, forwarded from the element doing the decoding. Named after
   * the media events they come from, so anyone who has written against
   * `<video>` already knows what they mean.
   */
  play: void;
  pause: void;
  ended: void;
  timeupdate: void;
  durationchange: void;
  volumechange: void;
  loadedmetadata: void;
  /** The element's `MediaError`, or whatever `play()` rejected with. */
  mediaerror: unknown;

  /**
   * Desktop: a gamepad button changed, before any mapping to a key. For game
   * code that wants "jump" rather than "Enter"; UI code should use the keys.
   */
  gamepadbutton: { pad: number; index: number; pressed: boolean };
  /** Desktop: a gamepad was plugged in or unplugged. */
  gamepadchanged: void;

  /** Desktop: `workAreaInsets` changed — a bar appeared, a taskbar hid. */
  workareachanged: void;
  /** Desktop: the theme was swapped. Anything mirroring it should re-read. */
  themechanged: void;
  /** Desktop lifecycle: a window was added, removed, minimized or restored. */
  windowschanged: void;
  activewindowchanged: void;

  /** Window lifecycle. */
  titlechanged: void;
  activated: void;
  deactivated: void;
  closing: { cancel: boolean };
  closed: void;
  /**
   * Window: minimized, and un-minimized again.
   *
   * The desktop's `windowschanged` says something moved; these say which
   * window and which way, which is what an application that wants to stop
   * doing work for a parked window — a video, a poll, a render loop — needs
   * to hear. A minimized window is still alive and still laid out; it is
   * only the drawing and the input that stop.
   */
  minimized: void;
  restored: void;
}

/**
 * The parts of a DOM mouse or pointer event that a `MouseEventArgs` is built
 * from. Named so a synthesized press — a long press standing in for a
 * right-click — can be described without faking a whole DOM event.
 */
export interface MouseSource {
  button?: number;
  buttons?: number;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
}

export function makeMouseArgs(
  x: number,
  y: number,
  screen: Point,
  source: MouseSource,
  clickCount = 1,
): MouseEventArgs {
  return {
    x,
    y,
    screen,
    button: source.button ?? 0,
    buttons: source.buttons ?? 0,
    ctrlKey: source.ctrlKey ?? false,
    shiftKey: source.shiftKey ?? false,
    altKey: source.altKey ?? false,
    metaKey: source.metaKey ?? false,
    clickCount,
    handled: false,
  };
}

export function makeKeyArgs(e: KeyboardEvent): KeyEventArgs {
  return {
    key: e.key,
    code: e.code,
    ctrlKey: e.ctrlKey,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
    metaKey: e.metaKey,
    repeat: e.repeat,
    handled: false,
  };
}
